import { FieldValueEdit } from '@databases/fields/FieldValueEdit';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import {
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { type DatabaseValue } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type DatabaseChange,
  type ValueChange,
} from '@databases/utils/database/changesets';
import { getInitialValue } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
} from '@databases/utils/database/utils';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch } from '@databases/utils/redux/redux';
import React, { forwardRef, useCallback } from 'react';

type DatabaseValueBulkEditProps = {
  contentId: string;
  value: DatabaseValue | undefined;
  onBulkEditChange: (value: DatabaseValue, change: ValueChange | null) => void;
};

const DatabaseValueBulkEdit = forwardRef<HTMLInputElement, DatabaseValueBulkEditProps>(
  function DatabaseValueBulkEdit({ contentId, value, onBulkEditChange }, ref) {
    const [firstSelectedField, selectedEntryIds, firstSelectedEntry] =
      defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
        const database = selectDatabaseByContentIdRequired(state, {
          contentId,
        });
        const selectedFieldIds = selectSelectedFieldIds(state, {
          contentId,
        });
        const selectedEntryIds = selectSelectedEntryIds(state, {
          contentId,
        });
        const firstSelectedField = getDatabaseFieldOptional(
          database,
          selectedFieldIds[0]
        );
        const firstSelectedEntry = getDatabaseEntryOptional(
          database,
          selectedEntryIds[0]
        );

        return [firstSelectedField, selectedEntryIds, firstSelectedEntry] as const;
      });

    const dispatch = defaultStoreUtils.useDispatch();
    const handleAddChangeToDraft = useCallback(
      (change: DatabaseChange) => {
        dispatch(addChangeToDraft(contentId, 'main', change));
      },
      [contentId, dispatch]
    );

    const store = defaultStoreUtils.useStore();
    const handleBulkEditValues = useCallback(
      (value: DatabaseValue, change: ValueChange | null) => {
        if (!firstSelectedField) {
          return;
        }
        onBulkEditChange(value, change);
        batch(() => {
          collabDatabaseBatch(store, contentId, () => {
            selectedEntryIds.forEach((entryId) => {
              handleAddChangeToDraft({
                type: DatabaseChangeType.SET_VALUE,
                fieldId: firstSelectedField.id,
                entryId: entryId,
                value: value,
                change,
              });
            });
          });
        });
      },
      [
        contentId,
        handleAddChangeToDraft,
        onBulkEditChange,
        selectedEntryIds,
        firstSelectedField,
        store,
      ]
    );

    if (!firstSelectedField) {
      return null;
    }

    return (
      <FieldValueEdit
        contentId={contentId}
        field={firstSelectedField}
        entryId={firstSelectedEntry?.id}
        value={value || getInitialValue(firstSelectedField.type)}
        isSeamless
        onChange={handleBulkEditValues}
        autoFocus
        addChangeToDraft={handleAddChangeToDraft}
        ref={ref}
        isBulkEdit={true}
      />
    );
  }
);

export default DatabaseValueBulkEdit;
